import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import useContentProtection from '../../hooks/useContentProtection';
import { 
  ArrowLeft,
  Play,
  Trophy,
  Target,
  Users,
  Phone,
  Video,
  Briefcase,
  Search,
  CheckCircle2,
  Lock,
  Star,
  Zap,
  Award,
  ChevronRight,
  Laptop,
  Heart,
  DollarSign,
  TrendingUp,
  Pill,
  Cog
} from 'lucide-react';

const RPATSimulations = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  
  useContentProtection(true);
  
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState(null);
  const [progress, setProgress] = useState(null);
  const [sourcingLabs, setSourcingLabs] = useState(null);
  const [selectedVertical, setSelectedVertical] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [catalogRes, progressRes, labsRes] = await Promise.all([
        api.get('/rpat/simulations/catalog'),
        api.get('/rpat/simulations/progress'),
        api.get('/rpat/simulations/sourcing-labs')
      ]);
      
      setCatalog(catalogRes.data);
      setProgress(progressRes.data);
      setSourcingLabs(labsRes.data);
      
      // Set selected vertical from progress if available
      if (progressRes.data?.selected_vertical) {
        setSelectedVertical(progressRes.data.selected_vertical);
      }
    } catch (err) {
      console.error('Failed to fetch simulation data:', err);
      setError(err.response?.data?.detail || 'Failed to load simulations');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const selectVertical = async (verticalId) => {
    try {
      await api.post(`/rpat/simulations/select-vertical?vertical_id=${verticalId}`);
      setSelectedVertical(verticalId);
      toast.success('Industry vertical selected!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to select vertical');
    }
  };

  const startSimulation = async (categoryId, difficulty) => {
    try {
      const res = await api.post('/rpat/simulations/start', null, {
        params: {
          simulation_type: categoryId,
          difficulty: difficulty,
          vertical_id: selectedVertical
        }
      });
      
      // Navigate to the simulation session
      navigate(`/simulation/${res.data.session_id}`, {
        state: { rpat: true, ...res.data }
      });
    } catch (err) {
      console.error('Failed to start simulation:', err);
      toast.error(err.response?.data?.detail || 'Failed to start simulation');
    }
  };

  // Start a sourcing lab session
  const startLabSession = async (lab) => {
    try {
      // Call the API to start a session for this lab
      const response = await api.post('/sourcing-labs/sessions/start', {
        lab_id: lab.id,
        difficulty: lab.difficulty || 'beginner'
      });
      
      if (response.data?.id) {
        toast.success('Lab session started!');
        navigate(`/sourcing-labs/session/${response.data.id}`);
      } else {
        throw new Error('No session ID returned');
      }
    } catch (err) {
      console.error('Failed to start lab:', err);
      const errorDetail = err.response?.data?.detail;
      
      // If there's an active session, offer to continue it
      if (errorDetail?.includes?.('active session') || err.response?.status === 409) {
        try {
          const activeRes = await api.get('/sourcing-labs/sessions/active');
          if (activeRes.data?.id) {
            toast.info('Continuing your active session');
            navigate(`/sourcing-labs/session/${activeRes.data.id}`);
            return;
          }
        } catch (activeErr) {
          console.error('Failed to fetch active session:', activeErr);
        }
      }
      
      toast.error(typeof errorDetail === 'string' ? errorDetail : 'Failed to start lab');
    }
  };

  const getVerticalIcon = (verticalId) => {
    const icons = {
      it: Laptop,
      engineering: Cog,
      healthcare: Heart,
      finance: DollarSign,
      sales: TrendingUp,
      pharma: Pill
    };
    return icons[verticalId] || Briefcase;
  };

  const getSimulationIcon = (simType) => {
    const icons = {
      client_call: Phone,
      candidate_call: Users,
      mock_interview: Video,
      full_desk: Briefcase
    };
    return icons[simType] || Target;
  };

  const getDifficultyColor = (difficulty, isCompleted, isUnlocked) => {
    if (isCompleted) return 'bg-emerald-500 text-white border-emerald-600';
    if (!isUnlocked) return 'bg-gray-100 text-gray-400 border-gray-200';
    
    const colors = {
      easy: 'bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200',
      medium: 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200',
      hard: 'bg-orange-100 text-orange-800 border-orange-300 hover:bg-orange-200',
      difficult: 'bg-red-100 text-red-800 border-red-300 hover:bg-red-200'
    };
    return colors[difficulty] || colors.easy;
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}
          </div>
          <Skeleton className="h-64" />
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="p-6">
          <Card className="max-w-md mx-auto">
            <CardContent className="pt-6 text-center">
              <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h2 className="text-xl font-semibold mb-2">Unable to Load Simulations</h2>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={() => navigate('/rpat')}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back to R-PAT
              </Button>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  const totalLevelsCompleted = progress?.overall?.total_levels_completed || 0;
  const totalRequired = 16; // 4 categories × 4 levels

  return (
    <MainLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/rpat')} data-testid="back-to-rpat">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
              R-PAT Simulations & Labs
            </h1>
            <p className="text-muted-foreground">
              Complete all 4 levels in each category to become a certified recruiter
            </p>
          </div>
        </div>

        {/* Progress Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Simulations</p>
                  <p className="text-2xl font-bold text-cyan-700">
                    {totalLevelsCompleted}/{totalRequired}
                  </p>
                </div>
                <Target className="h-8 w-8 text-cyan-600" />
              </div>
              <Progress value={(totalLevelsCompleted / totalRequired) * 100} className="mt-2 h-2" />
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Sourcing Labs</p>
                  <p className="text-2xl font-bold text-violet-700">
                    {sourcingLabs?.completed_count || 0}/10
                  </p>
                </div>
                <Search className="h-8 w-8 text-violet-600" />
              </div>
              <Progress value={(sourcingLabs?.completed_count || 0) * 10} className="mt-2 h-2" />
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg Score</p>
                  <p className="text-2xl font-bold text-amber-700">
                    {Math.round(progress?.overall?.average_score || 0)}%
                  </p>
                </div>
                <Star className="h-8 w-8 text-amber-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Categories Done</p>
                  <p className="text-2xl font-bold text-emerald-700">
                    {progress?.overall?.categories_complete || 0}/4
                  </p>
                </div>
                <Award className="h-8 w-8 text-emerald-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="simulations" className="space-y-4">
          <TabsList className="grid grid-cols-3 w-full max-w-md">
            <TabsTrigger value="simulations" data-testid="tab-simulations">Simulations</TabsTrigger>
            <TabsTrigger value="sourcing" data-testid="tab-sourcing">Sourcing Labs</TabsTrigger>
            <TabsTrigger value="vertical" data-testid="tab-vertical">Job Vertical</TabsTrigger>
          </TabsList>

          {/* Simulations Tab - Progressive Levels */}
          <TabsContent value="simulations" className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>How it works:</strong> Complete all 4 difficulty levels (Easy → Medium → Hard → Difficult) in each simulation category. 
                You must pass each level before unlocking the next.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {catalog?.categories?.map((category) => {
                const Icon = getSimulationIcon(category.id);
                const categoryProgress = progress?.categories?.[category.id];
                
                return (
                  <Card key={category.id} className="overflow-hidden hover:shadow-lg transition-all duration-200 hover:-translate-y-1">
                    <CardHeader className="bg-gradient-to-r from-slate-50 to-gray-50 border-b">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 text-white">
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-lg">{category.name}</CardTitle>
                          <CardDescription className="text-xs">
                            {category.duration_minutes} min per level
                          </CardDescription>
                        </div>
                        <Badge variant={category.completed_count === 4 ? 'default' : 'secondary'}>
                          {category.completed_count}/4
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <p className="text-sm text-muted-foreground mb-4">
                        {category.description}
                      </p>
                      
                      {/* Level Progression */}
                      <div className="grid grid-cols-4 gap-2">
                        {category.levels?.map((level, idx) => (
                          <div key={level.id} className="text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              className={`w-full h-16 flex flex-col items-center justify-center gap-1 ${
                                getDifficultyColor(level.id, level.completed, level.unlocked)
                              }`}
                              disabled={!level.unlocked || level.completed}
                              onClick={() => startSimulation(category.id, level.id)}
                              data-testid={`sim-${category.id}-${level.id}`}
                            >
                              {level.completed ? (
                                <CheckCircle2 className="h-5 w-5" />
                              ) : !level.unlocked ? (
                                <Lock className="h-5 w-5" />
                              ) : (
                                <Play className="h-5 w-5" />
                              )}
                              <span className="text-xs font-medium capitalize">{level.name}</span>
                            </Button>
                            {idx < 3 && (
                              <ChevronRight className="h-4 w-4 mx-auto mt-1 text-muted-foreground" />
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {categoryProgress?.is_complete && (
                        <div className="mt-4 p-2 bg-emerald-50 rounded-lg text-center">
                          <p className="text-sm text-emerald-700 font-medium flex items-center justify-center gap-2">
                            <Trophy className="h-4 w-4" />
                            Category Complete!
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Sourcing Labs Tab */}
          <TabsContent value="sourcing" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Sourcing Labs</h3>
                <p className="text-sm text-muted-foreground">
                  Complete 10 sourcing labs to master candidate identification
                </p>
              </div>
              <Badge variant="secondary" className="text-lg px-4 py-2">
                {sourcingLabs?.completed_count || 0} / 10
              </Badge>
            </div>

            <Progress 
              value={(sourcingLabs?.completed_count || 0) * 10} 
              className="h-3"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sourcingLabs?.labs?.map((lab) => (
                <Card 
                  key={lab.id}
                  className={`hover:shadow-md transition-shadow ${
                    lab.completed ? 'border-emerald-500 bg-emerald-50/50' : ''
                  }`}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex gap-2">
                        <Badge variant="outline" className="text-xs capitalize">
                          {lab.industry}
                        </Badge>
                        <Badge variant="secondary" className="text-xs capitalize">
                          {lab.difficulty}
                        </Badge>
                      </div>
                      {lab.completed && (
                        <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                      )}
                    </div>
                    <h4 className="font-semibold mb-1">{lab.title}</h4>
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {lab.description}
                    </p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                      <span>{lab.duration_minutes} min</span>
                      <span>Target: {lab.target_profiles} profiles</span>
                    </div>
                    <Button 
                      size="sm" 
                      variant={lab.completed ? 'outline' : 'default'}
                      className="w-full"
                      onClick={() => startLabSession(lab)}
                      data-testid={`lab-${lab.id}`}
                    >
                      {lab.completed ? 'Practice Again' : 'Start Lab'}
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </CardContent>
                </Card>
              ))}

              {(!sourcingLabs?.labs || sourcingLabs.labs.length === 0) && (
                <Card className="col-span-full p-8 text-center">
                  <Search className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">
                    No sourcing labs available yet. Contact admin to seed labs.
                  </p>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Job Vertical Tab */}
          <TabsContent value="vertical" className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>Choose your specialization:</strong> Select ONE industry vertical and complete all job simulations 
                within that vertical. This demonstrates deep industry knowledge for your certification.
              </p>
            </div>

            {selectedVertical && (
              <Card className="bg-gradient-to-r from-cyan-50 to-blue-50 border-cyan-200">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    {(() => {
                      const Icon = getVerticalIcon(selectedVertical);
                      const vertical = catalog?.verticals?.find(v => v.id === selectedVertical);
                      return (
                        <>
                          <Icon className="h-8 w-8 text-cyan-600" />
                          <div>
                            <p className="font-semibold text-cyan-900">Selected: {vertical?.name}</p>
                            <p className="text-sm text-cyan-700">{vertical?.description}</p>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </CardContent>
              </Card>
            )}

            <h3 className="text-lg font-semibold">Select Your Industry Vertical</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {catalog?.verticals?.map((vertical) => {
                const Icon = getVerticalIcon(vertical.id);
                const isSelected = selectedVertical === vertical.id;
                
                return (
                  <Card
                    key={vertical.id}
                    className={`cursor-pointer transition-all ${
                      isSelected
                        ? 'ring-2 ring-cyan-500 bg-cyan-50'
                        : 'hover:shadow-md hover:border-cyan-200'
                    }`}
                    onClick={() => !selectedVertical && selectVertical(vertical.id)}
                    data-testid={`vertical-${vertical.id}`}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${
                          isSelected 
                            ? 'bg-cyan-500 text-white' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          <Icon className="h-6 w-6" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold">{vertical.name}</h4>
                          <p className="text-xs text-muted-foreground">{vertical.description}</p>
                        </div>
                        {isSelected && (
                          <CheckCircle2 className="h-5 w-5 text-cyan-500" />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {selectedVertical && (
              <p className="text-sm text-muted-foreground text-center">
                You&apos;ve committed to {catalog?.verticals?.find(v => v.id === selectedVertical)?.name}. 
                Complete all simulations in this vertical for certification.
              </p>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default RPATSimulations;
